/**
 * Shared date formatting helpers, powered by date-fns with the active
 * vue-i18n locale.
 *
 * Rationale: `toLocaleDateString(undefined, ...)` silently follows the
 * browser locale, which drifts from vue-i18n, so FR UIs used to render
 * "Feb 3" and "55m ago". Always route through here.
 *
 * Vue callers: `const { formatRelative, formatDate } = useDateFormat()`.
 * Non-Vue callers: pass a date-fns `Locale` explicitly, or a vue-i18n
 * tag via the `*ByTag` variants.
 */

import {
  format as dfFormat,
  formatDistanceToNowStrict,
  isSameDay,
  isSameMonth,
  isSameYear,
  type Locale,
} from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { computed, unref } from 'vue'
import { useI18n } from 'vue-i18n'

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/
const UTC_MIDNIGHT_RE = /T00:00:00(\.\d+)?Z?$/

export type DateInput = string | Date | number | null | undefined

/**
 * Resolve a vue-i18n locale tag (e.g., `fr`, `fr-CA`, `en`, `en-US`) to the
 * date-fns `Locale` object. Defaults to `enUS`.
 */
export function resolveDateFnsLocale(tag: string | null | undefined): Locale {
  if (!tag) return enUS
  const lower = tag.toLowerCase()
  if (lower.startsWith('fr')) return fr
  return enUS
}

/**
 * True when the string is a plain YYYY-MM-DD (no time) or an ISO whose time
 * part is exactly UTC midnight — both should render date-only to avoid the
 * previous-day rollover west of UTC.
 */
export function isDateOnly(value: string): boolean {
  return DATE_ONLY_RE.test(value) || UTC_MIDNIGHT_RE.test(value)
}

/**
 * Parse a date string, Date, or epoch ms. Treats YYYY-MM-DD as local midnight
 * rather than UTC midnight. Returns null for unparseable input.
 */
export function parseDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'number') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match) {
    const local = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    return isNaN(local.getTime()) ? null : local
  }
  const parsed = new Date(value)
  return isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Serialize a Date to YYYY-MM-DD using local calendar components (for form
 * payloads tied to the viewer's calendar day).
 */
export function toLocalDateOnly(value: DateInput): string | null {
  const date = parseDate(value)
  if (!date) return null
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Serialize a Date to YYYY-MM-DD using UTC components (for payloads that
 * must align on UTC, e.g., shared cross-tz date keys).
 */
export function toUtcDateOnly(value: DateInput): string | null {
  const date = parseDate(value)
  if (!date) return null
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Compact relative time for space-constrained UI (e.g., sidebars).
 *
 * - < 1 min: "just now" / "à l'instant"
 * - < 1 h:   "55m"
 * - < 24 h:  "3h"
 * - 1 day:   "yesterday" / "hier"
 * - < 7 d:   "3d"
 * - older:   locale-aware medium date
 */
export function formatCompactRelative(value: DateInput, locale: Locale): string {
  const date = parseDate(value)
  if (!date) return ''
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMins < 1) return locale === fr ? 'à l’instant' : 'just now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays === 1) return locale === fr ? 'hier' : 'yesterday'
  if (diffDays < 7) return `${diffDays}d`
  return formatDay(date, locale)
}

/**
 * Relative time ("maintenant", "il y a 3 min", "mar.", "3 févr.", "Feb 3 2024").
 *
 * - < 1 min: "just now" / "à l'instant"
 * - < 1 day: `formatDistanceToNowStrict` with `addSuffix`
 * - < 1 week: weekday abbreviation
 * - same year: month + day
 * - older: month + day + year
 */
export function formatRelative(value: DateInput, locale: Locale): string {
  const date = parseDate(value)
  if (!date) return ''
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  if (diff < 60_000) {
    // date-fns's `formatDistanceToNowStrict` returns "0 seconds ago" below
    // the threshold, which reads awkward — prefer the localized "now" token.
    return locale === fr ? 'à l’instant' : 'just now'
  }
  if (diff < 86_400_000) {
    return formatDistanceToNowStrict(date, { addSuffix: true, locale })
  }
  if (diff < 604_800_000) {
    return dfFormat(date, 'EEE', { locale })
  }
  if (isSameYear(date, now)) {
    return dfFormat(date, 'MMM d', { locale })
  }
  return dfFormat(date, 'MMM d, yyyy', { locale })
}

/**
 * Unsuffixed distance ("3 hours", "2 days") — use for deltas where the
 * direction is implied by context.
 */
export function formatDistance(value: DateInput, locale: Locale): string {
  const date = parseDate(value)
  if (!date) return ''
  return formatDistanceToNowStrict(date, { locale })
}

/** Full date. Day-only strings skip the time component. */
export function formatDate(value: DateInput, locale: Locale): string {
  if (value === null || value === undefined || value === '') return ''
  const date = parseDate(value)
  if (!date) return typeof value === 'string' ? value : ''
  if (typeof value === 'string' && isDateOnly(value)) {
    return dfFormat(date, 'd MMM yyyy', { locale })
  }
  return dfFormat(date, 'd MMM yyyy, HH:mm', { locale })
}

/** Medium date + short time ("22 avr. 2026, 19:50" / "Apr 22, 2026, 19:50"). */
export function formatDateTime(value: DateInput, locale: Locale): string {
  const date = parseDate(value)
  if (!date) return ''
  return dfFormat(date, 'd MMM yyyy, HH:mm', { locale })
}

/** Date only, medium style ("22 avr. 2026" / "Apr 22, 2026"). */
export function formatDay(value: DateInput, locale: Locale): string {
  const date = parseDate(value)
  if (!date) return ''
  return dfFormat(date, 'd MMM yyyy', { locale })
}

/**
 * Long full date with weekday + 24h time ("mercredi 22 avril 2026, 19:50" /
 * "Wednesday April 22, 2026, 19:50"). Use on detail pages.
 */
export function formatLongDateTime(value: DateInput, locale: Locale): string {
  const date = parseDate(value)
  if (!date) return ''
  return dfFormat(date, 'EEEE d MMMM yyyy, HH:mm', { locale })
}

/** Time only in 24-hour ("19:50"). */
export function formatTime(value: DateInput, locale: Locale): string {
  const date = parseDate(value)
  if (!date) return ''
  return dfFormat(date, 'HH:mm', { locale })
}

/** Day + month, no year ("22 avr." / "Apr 22"). */
export function formatDayMonth(value: DateInput, locale: Locale): string {
  const date = parseDate(value)
  if (!date) return ''
  return dfFormat(date, 'd MMM', { locale })
}

export type WeekdayStyle = 'short' | 'long'

/** Weekday name ("mar." short / "mardi" long). */
export function formatWeekday(
  value: DateInput,
  locale: Locale,
  style: WeekdayStyle = 'short',
): string {
  const date = parseDate(value)
  if (!date) return ''
  return dfFormat(date, style === 'long' ? 'EEEE' : 'EEE', { locale })
}

/**
 * Compact date range:
 * - same day     → single `formatDay`
 * - same month   → "1 – 5 avr. 2026"
 * - same year    → "1 avr. – 3 mai 2026"
 * - cross year   → "28 déc. 2025 – 3 janv. 2026"
 * Returns '' when either bound is unparseable.
 */
export function formatDateRange(start: DateInput, end: DateInput, locale: Locale): string {
  const s = parseDate(start)
  const e = parseDate(end)
  if (!s || !e) return ''
  if (isSameDay(s, e)) {
    return dfFormat(s, 'd MMM yyyy', { locale })
  }
  if (isSameMonth(s, e)) {
    return `${dfFormat(s, 'd', { locale })} – ${dfFormat(e, 'd MMM yyyy', { locale })}`
  }
  if (isSameYear(s, e)) {
    return `${dfFormat(s, 'd MMM', { locale })} – ${dfFormat(e, 'd MMM yyyy', { locale })}`
  }
  return `${dfFormat(s, 'd MMM yyyy', { locale })} – ${dfFormat(e, 'd MMM yyyy', { locale })}`
}

/**
 * Tag-driven variants — use when the caller has a vue-i18n locale tag but no
 * resolved `Locale` object handy (e.g., plain-TS composables).
 */
export const formatCompactRelativeByTag = (v: DateInput, tag: string | null | undefined) =>
  formatCompactRelative(v, resolveDateFnsLocale(tag))
export const formatRelativeByTag = (v: DateInput, tag: string | null | undefined) =>
  formatRelative(v, resolveDateFnsLocale(tag))
export const formatDateByTag = (v: DateInput, tag: string | null | undefined) =>
  formatDate(v, resolveDateFnsLocale(tag))
export const formatDateTimeByTag = (v: DateInput, tag: string | null | undefined) =>
  formatDateTime(v, resolveDateFnsLocale(tag))
export const formatDayByTag = (v: DateInput, tag: string | null | undefined) =>
  formatDay(v, resolveDateFnsLocale(tag))
export const formatLongDateTimeByTag = (v: DateInput, tag: string | null | undefined) =>
  formatLongDateTime(v, resolveDateFnsLocale(tag))
export const formatTimeByTag = (v: DateInput, tag: string | null | undefined) =>
  formatTime(v, resolveDateFnsLocale(tag))
export const formatDayMonthByTag = (v: DateInput, tag: string | null | undefined) =>
  formatDayMonth(v, resolveDateFnsLocale(tag))
export const formatWeekdayByTag = (
  v: DateInput,
  tag: string | null | undefined,
  style: WeekdayStyle = 'short',
) => formatWeekday(v, resolveDateFnsLocale(tag), style)
export const formatDistanceByTag = (v: DateInput, tag: string | null | undefined) =>
  formatDistance(v, resolveDateFnsLocale(tag))
export const formatDateRangeByTag = (
  s: DateInput,
  e: DateInput,
  tag: string | null | undefined,
) => formatDateRange(s, e, resolveDateFnsLocale(tag))

/**
 * Vue composable — returns helpers bound to the active vue-i18n locale,
 * plus the resolved date-fns `Locale` for callers that need to drive
 * date-fns directly (e.g., `format(d, 'PPPP', { locale })`).
 */
export function useDateFormat() {
  const { locale } = useI18n()
  const dfLocale = computed(() => resolveDateFnsLocale(unref(locale) as string | undefined))

  return {
    locale: dfLocale,
    formatCompactRelative: (value: DateInput) => formatCompactRelative(value, dfLocale.value),
    formatRelative: (value: DateInput) => formatRelative(value, dfLocale.value),
    formatDate: (value: DateInput) => formatDate(value, dfLocale.value),
    formatDateTime: (value: DateInput) => formatDateTime(value, dfLocale.value),
    formatDay: (value: DateInput) => formatDay(value, dfLocale.value),
    formatLongDateTime: (value: DateInput) => formatLongDateTime(value, dfLocale.value),
    formatTime: (value: DateInput) => formatTime(value, dfLocale.value),
    formatDayMonth: (value: DateInput) => formatDayMonth(value, dfLocale.value),
    formatWeekday: (value: DateInput, style: WeekdayStyle = 'short') =>
      formatWeekday(value, dfLocale.value, style),
    formatDistance: (value: DateInput) => formatDistance(value, dfLocale.value),
    formatDateRange: (start: DateInput, end: DateInput) =>
      formatDateRange(start, end, dfLocale.value),
    isDateOnly,
    parseDate,
    toLocalDateOnly,
    toUtcDateOnly,
  }
}
